/* eslint-disable max-lines */
/* eslint-disable max-depth */
/* eslint-disable max-lines-per-function */
import {
  ComputeCaseDto,
  ComputeJobStatus,
  ComputeMethod,
  ComputeType,
  CreateComputeCaseCommandResponse,
  InputValueType,
  ListComputeCasesByAnalogueModelIdQueryResponse,
  ListComputeSettingsModelDto,
  ModelAreaDto,
  postApiV1JobsCancel,
  PostCancelJobCommand,
} from '../../../../../api/generated';
import { CaseButtons } from '../CaseButtons/CaseButtons';
import { CaseSettingSelect } from '../CaseSettingSelects/CaseSettingSelect';
import { ModelAreaSelect } from '../CaseSettingSelects/ModelAreaSelect';
import { VariogramOptionSelect } from '../VariogramSettingSelect/VariogramSettingSelect';
import * as Styled from './CaseRow.Styled';
import { useModelArea } from './hooks/useModelArea';
import { useSetSaved } from './hooks/useSetSaved';
import { useCaseParameters } from './hooks/useCaseParameters';
import { useObjectEstimationAvailability } from './hooks/useObjectEstimationAvailability';
import { useAvailableDefault } from './hooks/useAvailableDefault';
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../../../../hooks/queryKeys';
import { useParams } from 'react-router-dom';
import { analogueModelDefault } from '../../../../../constants/analogueModelDefault';
import { useGetParameterList } from './hooks/useGetParameterList';
import { useFetchModel } from '../../../../../hooks/useFetchModel';
import { useFetchComputeSettings } from '../../../../../hooks/useFetchComputeSettings';
import { useErrorStore } from '../../../../../stores/ErrorStore';
import {
  checkDuplicateType,
  validateContiniousParameter,
  validateIndicator,
  validateModelArea,
  validateNetToGross,
  validateObjectEstimation,
  validateVariogramDuplicate,
} from './hooks/CaseRow.hooks';
import { useCaseRowStore } from '../../../../../stores/CaseRowStore';
import { Variants } from '@equinor/eds-core-react/dist/types/components/types';
import {
  getObjectArchelSettings,
  isObjectModelAreaTaken,
} from '../../../utils/objectCaseUtils';

export const CaseRow = ({
  rowCase,
  id,
  allCasesList,
  caseList,
  caseType,
  saveCase,
  setAlertMessage,
  runCase,
  updateCase,
  deleteCase,
  removeLocalCase,
  settingsFilter,
  duplicateCase,
  isVariogramExhausted = false,
}: {
  rowCase: ComputeCaseDto;
  id: string;
  allCasesList: ComputeCaseDto[];
  caseList: ComputeCaseDto[];
  caseType: string;
  saveCase: (
    modelAreaId: string,
    computeMethod: ComputeMethod,
    computeType: ComputeType,
    inputSettings: string[],
  ) => Promise<CreateComputeCaseCommandResponse | undefined>;
  updateCase?: (
    modelAreaId: string,
    computeTypeId: string,
    inputSettings: string[],
  ) => Promise<ListComputeCasesByAnalogueModelIdQueryResponse | undefined>;
  deleteCase: (
    computeCaseId: string,
  ) => Promise<ListComputeCasesByAnalogueModelIdQueryResponse | undefined>;
  setAlertMessage: (message: string) => void;
  runCase: (id: string) => void;
  removeLocalCase: (id: string) => void;
  settingsFilter: (
    name: string,
    computeType: ComputeType,
  ) => ListComputeSettingsModelDto[] | undefined;
  duplicateCase?: (id: string) => void;
  isVariogramExhausted?: boolean;
}) => {
  const { modelId } = useParams<{ modelId: string }>();
  const queryClient = useQueryClient();
  const { data: analogueModel } = useFetchModel();
  const { data: computeSettings } = useFetchComputeSettings();
  const { addError } = useErrorStore();
  const {
    setIndicatorParams,
    setIndicatorVariogramModel,
    setIndicatorModelArea,
    setNetToGrossGrain,
    setNetToGrossVariogramModel,
    setNetToGrossModelArea,
    setContParamParameters,
    setContParamVariogramModel,
    setContParamModelArea,
    setObjectModelArea,
    setChannelModelArea,
    channelModelArea,
    objectModelArea,
    objectEstimationModelArea,
    objectEstimationArchel,
    setObjectEstimationModelArea,
    setObjectEstimationArchel,
    resetStates,
  } = useCaseRowStore();

  // const indicatorSettings = settingsFilter('Indicator');
  // const netToGrossSettings = settingsFilter('Net-To-Gross');
  // const continiousParameterSettings = settingsFilter('ContiniousParameter');

  const row = allCasesList.filter((c) => c.computeCaseId === id);
  const settingMethodType = row[0].computeMethod;

  const { saved } = useSetSaved(id, allCasesList, caseList);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const editSnapshotRef = useRef<{
    modelArea: ModelAreaDto[] | undefined;
    indicatorParameters: ListComputeSettingsModelDto[] | undefined;
    grainSize: ListComputeSettingsModelDto[] | undefined;
    continiousParameters: ListComputeSettingsModelDto[] | undefined;
    archelFilter: ListComputeSettingsModelDto[] | undefined;
    variogramModels: ListComputeSettingsModelDto[] | undefined;
  } | null>(null);
  const {
    selectedIndicatorParameters,
    selectedGrainSize,
    selectedContiniousParameters,
    selectedArchelFilter,
    selectedVariogramModels,
    setIndicatorParameters,
    setGrainSize,
    setContiniousParameters,
    setArchelFilter,
    setVariogramModels,
    selectedValues,
  } = useCaseParameters(rowCase, computeSettings);

  const { areaList, selectedModelArea, setModelArea, selectedRowArea } =
    useModelArea(allCasesList);

  const objectEstimationArchelSettings = getObjectArchelSettings(
    computeSettings?.objectComputeSettings,
  );

  // Object Estimation availability matrix: prevents duplicate (modelArea, archel) cases
  const objectEstimationCases = caseList.filter(
    (c) => c.computeMethod === ComputeMethod.OBJECT_ESTIMATION,
  );
  const {
    isModelAreaDisabled,
    isModelAreaFullyUsed,
    isDuplicateCombination,
    isArchelTaken,
  } = useObjectEstimationAvailability(
    objectEstimationCases,
    areaList ?? [],
    objectEstimationArchelSettings,
    id,
  );

  const objectEstimationModelAreaDisabled = (option: ModelAreaDto) =>
    isModelAreaDisabled(option, selectedArchelFilter);

  const objectEstimationArchelDisabled = (
    option: ListComputeSettingsModelDto,
  ) => isArchelTaken(option, selectedModelArea);

  const isNewObjectRow =
    rowCase.computeType === ComputeType.OBJECT &&
    !saved &&
    !caseList.some((existingCase) => existingCase.computeCaseId === id) &&
    Boolean(analogueModel);
  const selectObjectModelArea = useAvailableDefault(
    isNewObjectRow,
    areaList,
    selectedModelArea,
    setModelArea,
    (area) =>
      settingMethodType === ComputeMethod.OBJECT_ESTIMATION
        ? isModelAreaFullyUsed(area)
        : isObjectModelAreaTaken(
            caseList,
            area.modelAreaId,
            settingMethodType,
            id,
          ),
  );
  const selectObjectArchel = useAvailableDefault(
    isNewObjectRow &&
      settingMethodType === ComputeMethod.OBJECT_ESTIMATION &&
      Boolean(selectedModelArea?.length),
    objectEstimationArchelSettings,
    selectedArchelFilter,
    setArchelFilter,
    objectEstimationArchelDisabled,
  );

  const indicatorSettings = computeSettings?.variogramComputeSettings?.filter(
    (val) => val.computeMethod === ComputeMethod.INDICATOR,
  );
  const netToGrossSettings = computeSettings?.variogramComputeSettings?.filter(
    (val) => val.computeMethod === ComputeMethod.NET_TO_GROSS,
  );
  const continiousParameterSettings =
    computeSettings?.variogramComputeSettings?.filter(
      (val) => val.computeMethod === ComputeMethod.CONTINIOUS_PARAMETER,
    );

  const { inputSettingsList } = useGetParameterList(
    settingMethodType,
    indicatorSettings,
    netToGrossSettings,
    continiousParameterSettings,
    selectedIndicatorParameters,
    selectedArchelFilter,
    selectedGrainSize,
    selectedContiniousParameters,
    selectedVariogramModels,
  );

  const runValidations = async () => {
    const ret = [];
    switch (settingMethodType) {
      case ComputeMethod.INDICATOR:
        ret.push(
          await validateIndicator(
            addError,
            setIndicatorParams,
            setIndicatorVariogramModel,
            selectedIndicatorParameters,
            selectedVariogramModels,
          ),
        );
        ret.push(
          await validateModelArea(
            addError,
            setIndicatorModelArea,
            selectedModelArea,
          ),
        );
        ret.push(
          await validateVariogramDuplicate(
            addError,
            setIndicatorModelArea,
            caseList,
            id,
            settingMethodType,
            selectedModelArea,
            selectedIndicatorParameters?.concat(selectedVariogramModels ?? []),
          ),
        );
        break;
      case ComputeMethod.NET_TO_GROSS:
        ret.push(
          await validateNetToGross(
            addError,
            setNetToGrossGrain,
            setNetToGrossVariogramModel,
            selectedGrainSize,
            selectedVariogramModels,
          ),
        );
        ret.push(
          await validateModelArea(
            addError,
            setNetToGrossModelArea,
            selectedModelArea,
          ),
        );
        ret.push(
          await validateVariogramDuplicate(
            addError,
            setNetToGrossModelArea,
            caseList,
            id,
            settingMethodType,
            selectedModelArea,
            selectedGrainSize?.concat(
              selectedArchelFilter ?? [],
              selectedVariogramModels ?? [],
            ),
          ),
        );
        break;
      case ComputeMethod.CONTINIOUS_PARAMETER:
        ret.push(
          await validateContiniousParameter(
            addError,
            setContParamParameters,
            setContParamVariogramModel,
            selectedContiniousParameters,
            selectedVariogramModels,
          ),
        );
        ret.push(
          await validateModelArea(
            addError,
            setContParamModelArea,
            selectedModelArea,
          ),
        );
        ret.push(
          await validateVariogramDuplicate(
            addError,
            setContParamModelArea,
            caseList,
            id,
            settingMethodType,
            selectedModelArea,
            selectedContiniousParameters?.concat(
              selectedArchelFilter ?? [],
              selectedVariogramModels ?? [],
            ),
          ),
        );
        break;
      case ComputeMethod.MOUTHBAR:
        ret.push(
          await validateModelArea(
            addError,
            setObjectModelArea,
            selectedModelArea,
          ),
        );
        ret.push(
          await checkDuplicateType(
            addError,
            setObjectModelArea,
            caseList,
            selectedModelArea,
            caseType,
          ),
        );
        break;
      case ComputeMethod.CHANNEL:
        ret.push(
          await validateModelArea(
            addError,
            setChannelModelArea,
            selectedModelArea,
          ),
        );
        ret.push(
          await checkDuplicateType(
            addError,
            setChannelModelArea,
            caseList,
            selectedModelArea,
            caseType,
          ),
        );

        break;
      case ComputeMethod.OBJECT_ESTIMATION:
        ret.push(
          await validateObjectEstimation(
            addError,
            setObjectEstimationModelArea,
            setObjectEstimationArchel,
            selectedModelArea,
            selectedArchelFilter,
            isDuplicateCombination,
          ),
        );
        break;
    }

    return !ret.includes(true);
  };

  const cancelJob = useMutation({
    mutationFn: (requestBody: PostCancelJobCommand) =>
      postApiV1JobsCancel({ body: requestBody }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modelCases(modelId as string),
      });
    },
  });

  const runCancelJob = async (computeCaseId: string) => {
    if (!modelId) return;
    const requestBody: PostCancelJobCommand = {
      modelId: modelId,
      computeCaseId: computeCaseId,
    };

    const res = await cancelJob.mutateAsync(requestBody);

    if (res.data?.success) {
      setAlertMessage('Canceled computing case');
    }
  };

  const runRowCase = () => {
    if (
      (id && rowCase.jobStatus === ComputeJobStatus.RUNNING) ||
      rowCase.jobStatus === ComputeJobStatus.WAITING
    )
      return runCancelJob(id);
    if (id) return runCase(id);
  };

  const handleSaveCase = async (id: string) => {
    let inputSettings = inputSettingsList;
    // Hardcode the values for mauthbar and channel computeMethod from the model archels. Only one setting per object
    const mouthbareComputeCaseSettings =
      computeSettings?.objectComputeSettings?.find(
        (x) => x.computeMethod === ComputeMethod.MOUTHBAR,
      )?.computeSettingId;
    const channelComputeCaseSettings =
      computeSettings?.objectComputeSettings?.find(
        (x) => x.computeMethod === ComputeMethod.CHANNEL,
      )?.computeSettingId;

    if (
      inputSettings.length === 0 &&
      rowCase.computeType === ComputeType.OBJECT &&
      mouthbareComputeCaseSettings !== undefined &&
      channelComputeCaseSettings !== undefined
    ) {
      switch (rowCase.computeMethod) {
        case ComputeMethod.MOUTHBAR:
          inputSettings = [mouthbareComputeCaseSettings];
          break;
        case ComputeMethod.CHANNEL:
          inputSettings = [channelComputeCaseSettings];
          break;
      }
    }

    // Checks if Case already exists in the db
    const caseExists = caseList.filter((c) => c.computeCaseId === id);
    if (caseExists.length > 0 && updateCase && (await runValidations())) {
      // Check if model area has changed
      // Check if the new settings already exists
      const row = allCasesList.filter((c) => c.computeCaseId === id);
      const modelArea = selectedModelArea
        ? selectedModelArea[0].modelAreaId
        : row[0].modelArea
        ? row[0].modelArea.modelAreaId
        : '';
      const res = await updateCase(
        modelArea,
        row[0].computeCaseId,
        inputSettings,
      );
      if (res?.success) {
        setAlertMessage('Case updated');
        setIsEditing(false);
        setHasChanges(false);
      }
    } else {
      const row = allCasesList.filter((c) => c.computeCaseId === id);

      // Check if the instance is an Object case and right data/methods is provided
      if (saveCase) {
        if (
          row[0] !== undefined &&
          selectedModelArea &&
          selectedModelArea[0]?.modelAreaId !== ''
        ) {
          if (await runValidations()) {
            const res = await saveCase(
              selectedModelArea[0].modelAreaId,
              row[0].computeMethod,
              row[0].computeType,
              inputSettings,
            );
            if (res?.success) {
              removeLocalCase(id);
              resetStates();
              setAlertMessage('New case saved');
            }
          }
        } else {
          if ((await runValidations()) && selectedModelArea !== undefined) {
            const res = await saveCase(
              '',
              row[0].computeMethod,
              row[0].computeType,
              inputSettings,
            );
            if (res?.success) {
              removeLocalCase(id);
              resetStates();
              setAlertMessage('New case saved');
            }
          }
        }
      }
    }
  };

  const hasUnsavedCase = (id: string) => {
    const caseMethod = allCasesList.filter((c) => c.computeCaseId === id)[0]
      .computeMethod;
    const methodList = allCasesList
      .filter((c) => c.computeMethod === caseMethod)
      .filter((i) => i.computeCaseId.length < 4);
    return methodList.length > 0;
  };

  const filterSettings = (
    settings: ListComputeSettingsModelDto[] | undefined,
    inputValueType: InputValueType,
  ) => {
    switch (inputValueType) {
      case InputValueType.INDICATOR:
        return settings
          ?.filter((setting) => setting.inputValueType === inputValueType)
          .filter((setting) => setting.value !== '0');
      case InputValueType.ARCHEL:
        return settings
          ?.filter((setting) => setting.inputValueType === inputValueType)
          .filter((setting) => setting.value !== '0');
      default:
        return settings?.filter(
          (setting) => setting.inputValueType === inputValueType,
        );
    }
  };

  const indicatorFamilySettings = filterSettings(
    indicatorSettings,
    InputValueType.VARIOGRAM_FAMILY_FILTER,
  );

  const indicatorIndicatorSettings = filterSettings(
    indicatorSettings,
    InputValueType.INDICATOR,
  );

  const netGrossGrainSizeSettings = filterSettings(
    netToGrossSettings,
    InputValueType.NET_TO_GROSS,
  );

  const netGrossArchelSettings = filterSettings(
    netToGrossSettings,
    InputValueType.ARCHEL,
  );

  const netGrossVariogramFamilySettings = filterSettings(
    netToGrossSettings,
    InputValueType.VARIOGRAM_FAMILY_FILTER,
  );

  const contParamsVariogramFamilySettings = filterSettings(
    continiousParameterSettings,
    InputValueType.VARIOGRAM_FAMILY_FILTER,
  );

  const contParamsParamsSettings = filterSettings(
    continiousParameterSettings,
    InputValueType.ATTRIBUTE_NAME,
  );

  const contParamsArchelSettings = filterSettings(
    continiousParameterSettings,
    InputValueType.ARCHEL,
  );

  const variantChannel = (): Variants | undefined => {
    if (
      !saved &&
      selectedModelArea?.filter((c) => c.modelAreaType !== '').length === 0 &&
      channelModelArea
    )
      return 'error';

    return undefined;
  };

  const variantObject = (): Variants | undefined => {
    if (
      !saved &&
      selectedModelArea?.filter((c) => c.modelAreaType === '').length !== 0 &&
      objectModelArea
    )
      return 'error';

    return undefined;
  };

  const variantObjectEstimation = (): Variants | undefined => {
    if (
      !saved &&
      selectedModelArea?.filter((c) => c.modelAreaType !== '').length === 0 &&
      objectEstimationModelArea
    )
      return 'error';

    return undefined;
  };

  const objectEstimationArchelVariant = (): Variants | undefined => {
    if (
      !saved &&
      (!selectedArchelFilter || selectedArchelFilter.length === 0) &&
      objectEstimationArchel
    )
      return 'error';

    return undefined;
  };

  if (analogueModel === analogueModelDefault) return <p>Loading ...</p>;

  return (
    <Styled.Case
      id={id}
      className={id.length <= 3 ? 'local-case' : ''}
      data-case-method={rowCase.computeMethod}
    >
      <Styled.CaseRow
        className={caseType === 'Object' ? 'Object' : 'Variogram'}
      >
        {rowCase.computeMethod === ComputeMethod.INDICATOR && (
          <VariogramOptionSelect
            rowCase={rowCase}
            modelAreas={areaList ? areaList : []}
            caseType={ComputeMethod.INDICATOR}
            indicatorFamilySettings={indicatorFamilySettings}
            indicatorIndicatorSettings={indicatorIndicatorSettings}
            selectedModelArea={selectedRowArea(rowCase.computeCaseId)}
            selectedIndicatorParameters={selectedIndicatorParameters}
            selectedVariogramModels={selectedVariogramModels}
            setModelArea={setModelArea}
            setIndicatorParameters={setIndicatorParameters}
            setVariogramModels={setVariogramModels}
            existingCases={caseList}
            saved={saved}
            isEditing={isEditing}
            setHasChanges={setHasChanges}
            selectedParamValue={selectedValues}
          />
        )}

        {rowCase.computeMethod === ComputeMethod.NET_TO_GROSS && (
          <VariogramOptionSelect
            rowCase={rowCase}
            modelAreas={areaList ? areaList : []}
            caseType={ComputeMethod.NET_TO_GROSS}
            NetGrossGrainSizeSettings={netGrossGrainSizeSettings}
            NetGrossVariogramFamilySettings={netGrossVariogramFamilySettings}
            NetGrossArchelSettings={netGrossArchelSettings}
            selectedModelArea={selectedRowArea(rowCase.computeCaseId)}
            selectedGrainSize={selectedGrainSize}
            selectedVariogramModels={selectedVariogramModels}
            selectedArchelFilter={selectedArchelFilter}
            setModelArea={setModelArea}
            setGrainSize={setGrainSize}
            setVariogramModels={setVariogramModels}
            setArchelFilter={setArchelFilter}
            existingCases={caseList}
            saved={saved}
            isEditing={isEditing}
            setHasChanges={setHasChanges}
            selectedParamValue={selectedValues}
          />
        )}

        {rowCase.computeMethod === ComputeMethod.CONTINIOUS_PARAMETER && (
          <VariogramOptionSelect
            rowCase={rowCase}
            modelAreas={areaList ? areaList : []}
            caseType={ComputeMethod.CONTINIOUS_PARAMETER}
            contParamsVariogramFamilySettings={
              contParamsVariogramFamilySettings
            }
            contParamsParamsSettings={contParamsParamsSettings}
            contParamsArchelSettings={contParamsArchelSettings}
            selectedModelArea={selectedRowArea(rowCase.computeCaseId)}
            selectedContiniousParameters={selectedContiniousParameters}
            selectedArchelFilter={selectedArchelFilter}
            selectedVariogramModels={selectedVariogramModels}
            setModelArea={setModelArea}
            setContiniousParameters={setContiniousParameters}
            setArchelFilter={setArchelFilter}
            setVariogramModels={setVariogramModels}
            existingCases={caseList}
            saved={saved}
            isEditing={isEditing}
            setHasChanges={setHasChanges}
            selectedParamValue={selectedValues}
          />
        )}

        {rowCase.computeMethod === ComputeMethod.CHANNEL && (
          <Styled.AutocompleteWrapper>
            <ModelAreaSelect
              disableSelect={saved}
              modelAreas={areaList ? areaList : []}
              selectedModelArea={selectedRowArea(rowCase.computeCaseId)}
              setModelArea={selectObjectModelArea}
              existingCases={caseList}
              variant={variantChannel()}
            />
          </Styled.AutocompleteWrapper>
        )}

        {rowCase.computeMethod === ComputeMethod.MOUTHBAR && (
          <Styled.AutocompleteWrapper>
            <ModelAreaSelect
              disableSelect={saved}
              modelAreas={areaList ? areaList : []}
              selectedModelArea={selectedRowArea(rowCase.computeCaseId)}
              setModelArea={selectObjectModelArea}
              existingCases={caseList}
              variant={variantObject()}
            />
          </Styled.AutocompleteWrapper>
        )}

        {rowCase.computeMethod === ComputeMethod.OBJECT_ESTIMATION && (
          <Styled.AutocompleteWrapper>
            <ModelAreaSelect
              disableSelect={saved}
              modelAreas={areaList ? areaList : []}
              selectedModelArea={selectedRowArea(rowCase.computeCaseId)}
              setModelArea={selectObjectModelArea}
              existingCases={caseList}
              variant={variantObjectEstimation()}
              customOptionDisabled={
                !saved ? objectEstimationModelAreaDisabled : undefined
              }
            />
            <CaseSettingSelect
              label="Architectural element"
              singleSelect
              readOnly={saved}
              options={objectEstimationArchelSettings}
              selectedValue={selectedArchelFilter}
              setValue={selectObjectArchel}
              variant={objectEstimationArchelVariant()}
              customOptionDisabled={
                !saved ? objectEstimationArchelDisabled : undefined
              }
            />
          </Styled.AutocompleteWrapper>
        )}

        <CaseButtons
          id={id}
          caseType={caseType === 'Object' ? 'Object' : 'Variogram'}
          saved={saved}
          caseStatus={rowCase.jobStatus}
          logFileExists={
            rowCase.logFilePath === null || rowCase.logFilePath.length === 0
              ? false
              : true
          }
          hasUnsavedCase={hasUnsavedCase(id)}
          isEditing={isEditing}
          hasChanges={hasChanges}
          onEditToggle={() => {
            if (isEditing) {
              // Cancel: restore snapshot
              if (editSnapshotRef.current) {
                setModelArea(editSnapshotRef.current.modelArea);
                setIndicatorParameters(
                  editSnapshotRef.current.indicatorParameters,
                );
                setGrainSize(editSnapshotRef.current.grainSize);
                setContiniousParameters(
                  editSnapshotRef.current.continiousParameters,
                );
                setArchelFilter(editSnapshotRef.current.archelFilter);
                setVariogramModels(editSnapshotRef.current.variogramModels);
                editSnapshotRef.current = null;
              }
              setIsEditing(false);
              setHasChanges(false);
            } else {
              // Start editing: save snapshot
              editSnapshotRef.current = {
                modelArea: selectedModelArea,
                indicatorParameters: selectedIndicatorParameters,
                grainSize: selectedGrainSize,
                continiousParameters: selectedContiniousParameters,
                archelFilter: selectedArchelFilter,
                variogramModels: selectedVariogramModels,
              };
              setIsEditing(true);
              setHasChanges(false);
            }
          }}
          saveCase={() => handleSaveCase(id)}
          runCase={runRowCase}
          deleteCase={deleteCase}
          removeLocalCase={removeLocalCase}
          setAlertMessage={setAlertMessage}
          duplicateCase={() => {
            duplicateCase && duplicateCase(id);
          }}
          isVariogramExhausted={isVariogramExhausted}
        />
      </Styled.CaseRow>
    </Styled.Case>
  );
};
